import {
  computeLinkExpiry,
  GoogleSsoSignUpForm,
  Landing,
  LINK_REPLACEMENT_WARNING,
  LinkSentResend,
  RESEND_NOW_MESSAGE,
} from '@verifime/components';
import { SsoPortal } from '@verifime/cognito-passwordless-auth';
import { getEmailFromUrl } from '@verifime/utils';
import { ReactNode, useMemo } from 'react';
import { TGoogleSsoSubmitEmail, useGoogleSsoSignUp } from './useGoogleSsoSignUp';

export type TGoogleSsoSignUpViewProps = {
  submitEmail: TGoogleSsoSubmitEmail;
  companyName: string;
  companyLogo: ReactNode;
  landingTitle: string;
  landingDescription: string;
  logo?: ReactNode;
  // This is a shared view, not an app page, so it cannot read the feature toggle itself
  // (ADR-0050): the caller resolves `isInertMagicLinkEnabled` and passes the result through.
  mobileNav?: boolean;
  ssoOrigin: string;
  ssoReturnPath: string;
  portalEnv: SsoPortal | undefined;
  onAuthMethodSelected: (method: 'GOOGLE' | 'MAGIC_LINK') => void;
  onLoginRedirect: (email: string) => void;
  emailFromUrl?: string;
  // Gates the ThrottledResend affordance in the sent-email slot. Same ADR-0050 constraint as
  // `mobileNav` above: this shared view cannot read `isInertMagicLinkEnabled` itself, so the
  // caller resolves the toggle and passes the result through. Required (not optional) so every
  // caller makes an explicit choice rather than silently defaulting to either state.
  isResendEnabled: boolean;
};

export default function GoogleSsoSignUpView({
  submitEmail,
  companyName,
  companyLogo,
  landingTitle,
  landingDescription,
  logo,
  mobileNav,
  ssoOrigin,
  ssoReturnPath,
  portalEnv,
  onAuthMethodSelected,
  onLoginRedirect,
  emailFromUrl,
  isResendEnabled,
}: Readonly<TGoogleSsoSignUpViewProps>) {
  const resolvedEmail = emailFromUrl !== undefined ? emailFromUrl : (getEmailFromUrl() ?? '');

  const emailLocked = Boolean(resolvedEmail);

  const flow = useGoogleSsoSignUp({
    submitEmail,
    initialEmail: resolvedEmail,
    emailLocked,
    ssoOrigin,
    ssoReturnPath,
    portalEnv,
    onAuthMethodSelected,
    onLoginRedirect,
  });

  // Only useGoogleSsoSignUp knows when the link was actually sent (it owns the submitEmail call);
  // this view's own job is just deriving the moment it stops working from that, the way LinkSent
  // itself never computes its own expiresAt (see LinkSent.tsx's own doc comment). Memoised on
  // flow.sentAt alone, so a re-render that is not itself a fresh send (isSubmitting toggling,
  // parent re-render, etc.) reuses the same computed instant rather than drifting forward with
  // the clock. The `?? new Date()` fallback only ever runs while flow.sentAt is still null (no
  // send has happened yet, ENTER_EMAIL still showing) - GoogleSsoSignUpForm never renders this
  // value in that state, so the fallback is here purely to satisfy the prop's required, non-
  // optional type, not because it is ever actually displayed.
  const expiresAt = useMemo(() => computeLinkExpiry(flow.sentAt ?? new Date()), [flow.sentAt]);

  return (
    <Landing
      title={landingTitle}
      description={landingDescription}
      logo={logo}
      mobileNav={mobileNav}
    >
      <GoogleSsoSignUpForm
        display={flow.display}
        companyName={companyName}
        companyLogo={companyLogo}
        emailLocked={flow.emailLocked}
        email={flow.email}
        onEmailChange={flow.onEmailChange}
        onContinue={flow.onContinue}
        isSubmitting={flow.isSubmitting}
        onGoogle={flow.onGoogle}
        googleLoading={flow.googleLoading}
        error={flow.error}
        expiresAt={expiresAt}
        // Task 3 ("Link sent, one screen", settled 2026-09-17): the placeholder ThrottledResend
        // affordance (unapproved copy, slot 15) is superseded now the approved copy exists.
        // isResendEnabled still gates the WHOLE affordance exactly as it did before (mirrors
        // apps/customer-portal/src/pages/index.tsx's identical `isInertMagicLinkEnabled ? {...} :
        // undefined` pattern): toggle off renders LinkSent with an empty resend slot, not the
        // pre-Task-3 screen, which is gone entirely, not toggle-gated.
        resendAction={
          isResendEnabled ? (
            <LinkSentResend
              leadLine="Didn't get it?"
              resendActionLabel="Resend the email"
              pressedInsideWindowMessage="Please try again later, we will let you know."
              replacementWarning={LINK_REPLACEMENT_WARNING}
              resendNowMessage={RESEND_NOW_MESSAGE}
              onResend={flow.onResend}
              isResending={flow.isSubmitting}
            />
          ) : undefined
        }
      />
    </Landing>
  );
}
